/**
 * App root — providers + router.
 *
 * Routes (all private except /login). Role access:
 *   /          Dashboard    (superadmin, sales, restricted)
 *   /sales     Ventas       (superadmin, sales, user)
 *   /returns   Devoluciones (superadmin, sales, user)
 *   /clients   Clientes     (superadmin, sales)
 *   /purchases Compras      (superadmin)
 *   /partners  Partners     (superadmin)
 *   /inventory Inventario   (superadmin)
 *   /reports   Reportes     (superadmin)
 */
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { AuthProvider } from '@/context/AuthContext'
import { RequireAuth } from '@/components/RequireAuth'
import { AppLayout } from '@/components/layout/AppLayout'
import LoginPage from '@/pages/Login'
import DashboardPage from '@/pages/Dashboard'
import SalesPage from '@/pages/Sales'
import PurchasesPage from '@/pages/Purchases'
import ClientsPage from '@/pages/Clients'
import PartnersPage from '@/pages/Partners'
import ReturnsPage from '@/pages/Returns'
import InventoryPage from '@/pages/Inventory'
import ProductsPage from '@/pages/Products'
import ReportsPage from '@/pages/Reports'
import DevPage from '@/pages/Dev'

const ROLES_ADMIN = ['superadmin']
const ROLES_SALES = ['superadmin', 'sales']
const ROLES_DASHBOARD = ['superadmin', 'sales', 'restricted']
const ROLES_USER = ['superadmin', 'sales', 'user']

function Guard({ roles, element }) {
  return <RequireAuth roles={roles}>{element}</RequireAuth>
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route
            element={
              <RequireAuth>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route index element={<Guard roles={ROLES_DASHBOARD} element={<DashboardPage />} />} />
            <Route path="sales" element={<Guard roles={ROLES_USER} element={<SalesPage />} />} />
            <Route path="returns" element={<Guard roles={ROLES_USER} element={<ReturnsPage />} />} />
            <Route path="clients" element={<Guard roles={ROLES_SALES} element={<ClientsPage />} />} />
            <Route path="purchases" element={<Guard roles={ROLES_ADMIN} element={<PurchasesPage />} />} />
            <Route path="partners" element={<Guard roles={ROLES_ADMIN} element={<PartnersPage />} />} />
            <Route path="inventory" element={<Guard roles={ROLES_ADMIN} element={<InventoryPage />} />} />
            <Route path="products" element={<Guard roles={ROLES_ADMIN} element={<ProductsPage />} />} />
            <Route path="reports" element={<Guard roles={ROLES_ADMIN} element={<ReportsPage />} />} />
            {import.meta.env.DEV && (
              <Route path="dev" element={<Guard roles={ROLES_ADMIN} element={<DevPage />} />} />
            )}
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  )
}
